import { BrandLogo } from "@/components/shared/BrandLogo";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { Loader2, Compass, Gift, ChevronRight } from "lucide-react";

const SOURCE_LABELS = {
  browse: "Browse (All Offers)",
  search: "Search",
  category: "Category Filter",
  top_picks: "Top Picks",
  expiring_soon: "Expiring Soon",
  best_value: "Best Value",
  trending: "Trending / Weather",
  trending_map: "Trending Map",
  favorites: "Favorites",
  tracker: "My Tracker",
  direct: "Direct Link",
};

const SOURCE_COLORS = {
  browse: "bg-sky-400",
  search: "bg-violet-400",
  category: "bg-teal-400",
  top_picks: "bg-amber-400",
  expiring_soon: "bg-red-400",
  best_value: "bg-green-400",
  trending: "bg-pink-400",
  trending_map: "bg-rose-400",
  favorites: "bg-red-300",
  tracker: "bg-indigo-400",
  direct: "bg-slate-400",
};

export default function DiscoveryAnalyticsPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/discovery-analytics`)
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-4">Failed to load</p>;

  const { total_discoveries, channels, top_discovered_offers } = data;

  if (total_discoveries === 0) {
    return (
      <div className="text-center py-8" data-testid="discovery-analytics-panel">
        <Compass className="w-8 h-8 text-slate-200 mx-auto mb-2" />
        <p className="text-sm text-slate-400">No discovery data yet</p>
        <p className="text-xs text-slate-300 mt-1">As users browse and click offers, you'll see how they find them</p>
      </div>
    );
  }

  const maxCount = channels.length > 0 ? channels[0].discoveries : 1;

  return (
    <div className="space-y-4" data-testid="discovery-analytics-panel">
      {/* Total */}
      <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-xl p-3 border border-indigo-100">
        <p className="text-xs text-indigo-600 font-medium">Total Discoveries</p>
        <p className="text-2xl font-bold text-indigo-800">{total_discoveries}</p>
        <p className="text-[10px] text-indigo-500 mt-0.5">How users navigate to offer detail pages</p>
      </div>

      {/* Channel breakdown */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-600 px-1">Discovery Channels</p>
        {channels.map((ch) => {
          const color = SOURCE_COLORS[ch.source] || "bg-slate-400";
          return (
            <div key={ch.source} className="flex items-center gap-3" data-testid={`discovery-channel-${ch.source}`}>
              <span className="text-xs font-medium text-slate-600 w-32 truncate">
                {SOURCE_LABELS[ch.source] || ch.source}
              </span>
              <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${color} transition-all duration-500`}
                  style={{ width: `${Math.max((ch.discoveries / maxCount) * 100, 4)}%` }}
                />
              </div>
              <span className="text-[10px] font-semibold text-slate-500 w-8 text-right">{ch.discoveries}</span>
              <span className="text-[10px] text-slate-400 w-10 text-right">{ch.pct}%</span>
            </div>
          );
        })}
      </div>

      {/* Top discovered offers */}
      {top_discovered_offers.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-600 px-1">Most Discovered Offers</p>
          {top_discovered_offers.slice(0, 5).map((o, i) => (
            <button
              key={o.business_id}
              onClick={() => navigate(`/offer/${o.business_id}`)}
              className="w-full flex items-center gap-2.5 p-2.5 rounded-xl bg-white border border-slate-100 hover:shadow-sm transition-shadow text-left"
              data-testid={`discovery-offer-${o.business_id}`}
            >
              <span className="text-xs font-bold text-slate-400 w-5">{i + 1}</span>
              <div className="w-7 h-7 rounded-lg overflow-hidden flex-shrink-0 bg-slate-50 flex items-center justify-center">
                <BrandLogo name={o.name} logoUrl={o.logo_url} size="sm" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-700 truncate">{o.name}</p>
                <p className="text-[10px] text-slate-400">via {o.sources.map(s => SOURCE_LABELS[s] || s).join(", ")}</p>
              </div>
              <span className="text-xs font-bold text-indigo-600">{o.discoveries}</span>
              <ChevronRight className="w-3 h-3 text-slate-300" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
